/**
 * Signed decimal-string arithmetic, for the numbers a stock count derives.
 *
 * `decimalString.ts` beside this file is the recipe editor's set and is
 * deliberately **unsigned** — every quantity a recipe holds is a quantity of
 * something. A count is the one screen whose derived values are signed by
 * nature: a variance is `counted − expected` and the interesting half of that
 * is negative. Nothing there subtracts or multiplies, so this is a new
 * operation rather than a second copy of an old one.
 *
 * The same rule applies as next door: nothing here calls `Number()` or
 * `parseFloat`. §21 quantities are `Decimal(12,3)` and costs `Decimal(12,4)`,
 * neither of which a JS double holds faithfully — `0.1 + 0.2` is the whole
 * argument. Values are scaled to a common integer by shifting the point, done
 * in `BigInt`, and shifted back. Exact by construction, at any scale.
 */

/** A decimal literal that may carry a leading minus. */
export const SIGNED_DECIMAL = /^-?\d+(\.\d+)?$/;

export function isSignedDecimal(value: string): boolean {
  return SIGNED_DECIMAL.test(value.trim());
}

/** Fraction digits carried by a decimal literal. */
function scaleOf(value: string): number {
  return (value.split('.')[1] ?? '').length;
}

/** The literal as an integer count of `10^-scale` units. */
function toUnits(value: string, scale: number): bigint {
  const text = value.trim();
  const negative = text.startsWith('-');
  const [whole, fraction = ''] = (negative ? text.slice(1) : text).split('.');
  const digits = whole + fraction.padEnd(scale, '0');
  const magnitude = BigInt(digits === '' ? '0' : digits);
  return negative ? -magnitude : magnitude;
}

/** Back to a literal, keeping every one of `scale`'s fraction digits. */
function fromUnits(units: bigint, scale: number): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(scale + 1, '0');
  const whole = digits.slice(0, digits.length - scale);
  const fraction = scale ? digits.slice(digits.length - scale) : '';
  const text = fraction ? `${whole}.${fraction}` : whole;
  return negative ? `-${text}` : text;
}

/**
 * Both operands are read at whichever of them carries more fraction digits, so
 * a `(12,3)` quantity and a `(12,4)` cost meet without either being rounded.
 * A malformed operand is read as zero rather than as `NaN`: this arithmetic
 * feeds a table cell, and a half-typed `-` must not blank the whole column.
 */
function combine(a: string, b: string, op: (x: bigint, y: bigint) => bigint): string {
  const left = isSignedDecimal(a) ? a.trim() : '0';
  const right = isSignedDecimal(b) ? b.trim() : '0';
  const scale = Math.max(scaleOf(left), scaleOf(right));
  return fromUnits(op(toUnits(left, scale), toUnits(right, scale)), scale);
}

export function addDecimal(a: string, b: string): string {
  return combine(a, b, (x, y) => x + y);
}

export function subtractDecimal(a: string, b: string): string {
  return combine(a, b, (x, y) => x - y);
}

/** Scales add, as they do in the arithmetic: `(12,3) × (12,4)` lands at 7dp. */
export function multiplyDecimal(a: string, b: string): string {
  const left = isSignedDecimal(a) ? a.trim() : '0';
  const right = isSignedDecimal(b) ? b.trim() : '0';
  const leftScale = scaleOf(left);
  const rightScale = scaleOf(right);
  return fromUnits(
    toUnits(left, leftScale) * toUnits(right, rightScale),
    leftScale + rightScale,
  );
}

export function sumDecimal(values: string[]): string {
  return values.reduce((total, value) => addDecimal(total, value), '0');
}

/** −1, 0 or 1. `0`, `-0.000` and `.` all mean zero. */
export function signOfDecimal(value: string): -1 | 0 | 1 {
  if (!isSignedDecimal(value)) return 0;
  const units = toUnits(value.trim(), scaleOf(value.trim()));
  return units === 0n ? 0 : units < 0n ? -1 : 1;
}

/**
 * Trailing zeros dropped for display, sign kept, without touching the value.
 * `formatQuantity` in `../shared` does the same job but strips only from the
 * right of a `.`, which is all an unsigned quantity ever needs.
 */
export function formatSignedQuantity(value: string): string {
  if (!isSignedDecimal(value)) return value;
  const text = value.trim();
  const trimmed = text.includes('.') ? text.replace(/0+$/, '').replace(/\.$/, '') : text;
  return trimmed === '-0' ? '0' : trimmed;
}
